import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Download, MapPin, Phone, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { formatINR } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicInvoice() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/invoice/${token}`);
        setData(data);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center max-w-md">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Invoice not found
          </h1>
          <p className="text-slate-600 mt-2">The link may have expired or been revoked.</p>
        </div>
      </div>
    );
  }

  const { invoice, clinic, patient, visit } = data;
  const isPaid = invoice.status === "paid";

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="public-invoice-page">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg shadow-slate-300/30 overflow-hidden border border-slate-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-sky-600 text-white p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }} data-testid="invoice-clinic-name">
                  {clinic.name}
                </h1>
                {clinic.address && (
                  <div className="text-sm text-blue-50 mt-2 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> {clinic.address}
                  </div>
                )}
                <div className="flex items-center gap-4 mt-1 text-sm text-blue-50">
                  {clinic.phone && (
                    <span className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" /> {clinic.phone}
                    </span>
                  )}
                  {clinic.gstin && <span>GSTIN: {clinic.gstin}</span>}
                </div>
              </div>
              <div className="text-right">
                <Badge variant="outline" className={`${isPaid ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"} text-sm font-semibold py-1 px-3`}>
                  {isPaid ? "PAID" : "UNPAID"}
                </Badge>
                <div className="text-xs uppercase tracking-wider text-blue-100 mt-3">Invoice</div>
                <div className="font-mono text-sm text-white" data-testid="invoice-number">
                  #{invoice.invoice_id?.slice(-12).toUpperCase()}
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-8">
            <div className="grid sm:grid-cols-2 gap-8 mb-8">
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider mb-2">Bill to</div>
                <div className="font-semibold text-slate-900 text-lg">{patient.name}</div>
                {patient.phone && <div className="text-sm text-slate-500 mt-0.5">{patient.phone}</div>}
              </div>
              <div className="sm:text-right">
                <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider mb-2">Date issued</div>
                <div className="font-semibold text-slate-900">
                  {new Date(invoice.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                </div>
                {invoice.paid_at && (
                  <div className="text-xs text-emerald-600 mt-0.5">
                    Paid on {new Date(invoice.paid_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                )}
              </div>
            </div>

            {/* Items table */}
            <div className="border border-slate-200 rounded-xl overflow-hidden mb-6">
              <div className="bg-slate-50 px-5 py-3 grid grid-cols-[1fr_auto] gap-4 text-xs uppercase tracking-wider font-semibold text-slate-500 border-b border-slate-200">
                <div>Description</div>
                <div className="text-right">Amount</div>
              </div>
              <div className="px-5 py-4 grid grid-cols-[1fr_auto] gap-4 text-sm">
                <div>
                  <div className="font-medium text-slate-900">Consultation</div>
                  {visit?.diagnosis && (
                    <div className="text-xs text-slate-500 mt-0.5">{visit.diagnosis}</div>
                  )}
                </div>
                <div className="text-right font-semibold">{formatINR(invoice.amount)}</div>
              </div>
            </div>

            {/* Total */}
            <div className="flex items-center justify-between py-4 border-t-2 border-slate-200">
              <div className="text-lg font-semibold text-slate-700" style={{ fontFamily: "Manrope" }}>Total</div>
              <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }} data-testid="invoice-total">
                {formatINR(invoice.amount)}
              </div>
            </div>

            {/* Visit notes (optional, light) */}
            {visit && (visit.symptoms || visit.prescription) && (
              <div className="mt-8 pt-6 border-t border-slate-100 grid sm:grid-cols-2 gap-6 text-sm">
                {visit.symptoms && (
                  <div>
                    <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider mb-1">Symptoms noted</div>
                    <div className="text-slate-700">{visit.symptoms}</div>
                  </div>
                )}
                {visit.prescription && (
                  <div>
                    <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider mb-1">Prescription</div>
                    <div className="text-slate-700 whitespace-pre-wrap">{visit.prescription}</div>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="mt-8 pt-6 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-slate-400">
                Generated by HeDesk{clinic.license_number ? ` · License: ${clinic.license_number}` : ""}
              </div>
              <a href={`${API}/public/invoice/${token}/pdf`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="invoice-download-pdf-btn">
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </a>
            </div>
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-slate-500">
          Powered by <a href="/" className="font-semibold text-slate-700 hover:text-blue-600">HeDesk</a> · Patient Manager for Indian Clinics
        </div>
      </div>
    </div>
  );
}
